import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import {
  resolveDesignTemplateForCampaign,
  resolveStoredDesignTemplateId
} from 'features/EmailEditor/campaignEmailCustomizationHelpers'
import InviteEmailDesignApplyTemplateModal from 'features/InviteEmailDesign/InviteEmailDesignApplyTemplateModal'
import InviteEmailDesignTemplatePreview from 'features/InviteEmailDesign/InviteEmailDesignTemplatePreview'
import campaignApi from 'services/api/campaign'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import emailEditorStyles from './EmailEditor.module.scss'

const CampaignEmailDesignTemplatePicker = ({
  contentId,
  relatedCampaignIds = [],
  campaignDescription = 'This campaign',
  onTemplateApplied
}) => {
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()

  const [templates, setTemplates] = useState([])
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true)
  const [appliedTemplateId, setAppliedTemplateId] = useState(null)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)

  useEffect(() => {
    let isMounted = true

    setIsLoadingTemplates(true)

    Promise.all([
      inviteEmailDesignApi.listTemplates(),
      contentId
        ? campaignApi.getCampaignEmail({ campaignId: contentId }).catch(() => null)
        : Promise.resolve(null)
    ])
      .then(([templateList, emailData]) => {
        if (!isMounted) {
          return
        }

        const nextTemplates = templateList?.templates || []
        setTemplates(nextTemplates)

        const resolvedTemplate = resolveDesignTemplateForCampaign(
          nextTemplates,
          emailData?.emailParams
        )

        if (resolvedTemplate?.id) {
          setAppliedTemplateId(resolvedTemplate.id)
          return
        }

        const storedTemplateId = resolveStoredDesignTemplateId(emailData?.emailParams)
        if (storedTemplateId) {
          setAppliedTemplateId(storedTemplateId)
        }
      })
      .catch(() => {
        if (isMounted) {
          setTemplates([])
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingTemplates(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [contentId])

  const appliedTemplate = useMemo(
    () => templates.find(template => template.id === appliedTemplateId) || null,
    [appliedTemplateId, templates]
  )

  function handleOpenTemplateModal() {
    if (promptEmailSubscriptionUpgrade()) {
      return
    }

    setIsTemplateModalOpen(true)
  }

  function handleTemplateModalClose() {
    setIsTemplateModalOpen(false)
  }

  function handleTemplateApplied({ templateId, templateName }) {
    setAppliedTemplateId(templateId)
    onTemplateApplied?.({ templateId, templateName })
  }

  if (isLoadingTemplates) {
    return null
  }

  if (!templates.length) {
    return (
      <div className={emailEditorStyles.templatePicker}>
        <div className={emailEditorStyles.templatePickerSummary}>
          <div className={emailEditorStyles.templatePickerSummaryContent}>
            <span className={emailEditorStyles.templatePickerSummaryName}>Email Template</span>
            <p className={emailEditorStyles.templatePickerEmptyState}>
              No email designs yet.&nbsp;
              <Link to="/invite-email-design">Create one first</Link>.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={emailEditorStyles.templatePicker}>
        <div className={emailEditorStyles.templatePickerSummary}>
          <div className={emailEditorStyles.templatePickerSummaryContent}>
            <span className={emailEditorStyles.templatePickerSummaryName}>
              {appliedTemplate?.name || 'Choose a saved email design'}
            </span>
            <span className={emailEditorStyles.templatePickerSummaryDescription}>
              {appliedTemplate
                ? 'Saved email design for this campaign. Message content is customized below.'
                : 'Select one of your saved email designs from Invite Email Design.'}
            </span>
            <SecondaryRegularButton
              className={emailEditorStyles.templatePickerChangeButton}
              onClick={handleOpenTemplateModal}
            >
              Change template
            </SecondaryRegularButton>
          </div>
          <div className={emailEditorStyles.templatePickerSummaryPreview}>
            {appliedTemplate ? (
              <InviteEmailDesignTemplatePreview
                templateId={appliedTemplate.id}
                variant="card"
                fitToContainer
                enabled
              />
            ) : (
              <div className={emailEditorStyles.templatePickerSummaryPlaceholder} />
            )}
          </div>
        </div>
      </div>

      <InviteEmailDesignApplyTemplateModal
        isOpen={isTemplateModalOpen}
        campaignId={contentId}
        relatedCampaignIds={relatedCampaignIds}
        initialTemplateId={appliedTemplateId}
        campaignDescription={campaignDescription}
        templates={templates}
        onClose={handleTemplateModalClose}
        onApplied={handleTemplateApplied}
      />
    </>
  )
}

export default CampaignEmailDesignTemplatePicker
